
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationProvider } from 'vee-validate';
import { DateTime } from 'luxon';
import Toast from '@/utils/toast';
import Scheduling from '@/utils/scheduling';
import DatePicker from '@/components/shared/DatePicker.vue';
import DeliveryAddress from './delivery/DeliveryAddress.vue';
import DeliveryOpenAddress from './delivery/DeliveryOpenAddress.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'orders';

const DEFAULT_EVENT_INFORMATION: EventInfo = {
	name: '',
	eventStartDate: '',
	eventEndDate: '',
	numPeople: 0,
	address: '',
	extra: ''
};

const DEFAULT_TAKEOUT_INFORMATION = {
	dueByDate: '',
	dueByTime: '',
	notes: '',
	prepTime: 0,
	scheduled: true
};

@Component<EventInformation>({
	components: {
		DatePicker,
		DeliveryAddress,
		DeliveryOpenAddress,
		SelectComponent,
		TextField,
		ValidationProvider
	}
})
export default class EventInformation extends Vue {
	@Action('fetchSchedulingRestrictions', { namespace }) private fetchSchedulingRestrictions!: (items: MenuOrderItem[]) => Promise<ScheduleRestrictions>;
	@Action('setEventInformation', { namespace }) private setEventInformation!: (eventInfo: EventInfo | null) => void;
	@Action('setTakeoutInformation', { namespace }) private setTakeoutInformation!: (takeoutInformation: Pickup) => void;
	@Action('setDeliveryInformation', { namespace }) private setDeliveryInformation!: (deliveryInfomration: DeliveryInfo) => void;
	@Getter('getTakeoutInformation', { namespace }) private takeoutInformation!: Pickup;
	@Getter('getEventInformation', { namespace }) private eventInformation!: EventInfo;
	@Getter('getOrderDetails', { namespace }) private orderDetails!: OrderDetails;
	@Getter('getCartItems', { namespace }) private items!: MenuOrderItem[];
	@Getter('getModifyingOrder', { namespace }) private modifyingOrder!: boolean;
	@Getter('getDeliveryInformation', { namespace }) private deliveryInformation!: DeliveryInfo | null;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getRestaurantDeliveryInfo', { namespace: 'auth' }) private restaurantDeliveryInfo!: Delivery | null;
	@Getter('hideTakeoutTime', { namespace: 'auth' }) private hideTakeoutTime!: boolean;
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;
	@Getter('getSelectedInquiryFormRequest', { namespace: 'inquiryForm' }) private request!: InquiryFormRequest;
	@Prop({ type: Boolean, default: false }) private hideHeader!: boolean;
	@Prop({ type: String, required: false, default: '' }) private coordinatesErrorMsg!: string;

	private event: EventInfo = { ...DEFAULT_EVENT_INFORMATION };
	private date: string = '';
	private startTime: string = '';
	private endTime: string = '';
	private takeout: Pickup = { ...DEFAULT_TAKEOUT_INFORMATION };
	private scheduling: Scheduling | null = null;
	private fetchingSchedulingInfo: boolean = true;

	/**
	 * Format dueByDate to expected format for the datepicker
	 *
	 * @return {string}
	 */
	private get dateSelectorFormat(): string {
		if (this.date) {
			return DateTime.fromISO(this.date).toISODate();
		}
		return '';
	}

	/**
	 * Return time options with times at or before the start time disabled.
	 *
	 * @return {{ value: string, disabled: boolean }[]}
	 */
	private get eventEndTimeOptions(): { value: string, disabled: boolean }[] {
		if (!this.scheduling?.timeOptions) {
			return [];
		}
		if (!this.startTime) {
			return this.scheduling.timeOptions;
		}
		const format = this.$t('orders.guest_details.takeout.timepicker_format');
		const startTime = DateTime.fromFormat(this.startTime, format);
		return this.scheduling.timeOptions.filter(option => DateTime.fromFormat(option.value, format) > startTime);
	}

	/**
	 * Set the takeout date if it's null and get the scheduling
	 * restrictions from the menus/restaurant.
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		this.$emit('fetching-scheduling-info', true);
		const format = this.$t('orders.guest_details.takeout.timepicker_format');
		if (this.request) {
			this.date = this.request.event_date;
			this.event.numPeople = this.request.num_people;
			this.takeout.dueByDate = this.request.event_date;
		}
		if (this.eventInformation) {
			this.event = { ...this.eventInformation };
			if (this.eventInformation.eventStartDate) {
				const start = DateTime.fromISO(this.eventInformation.eventStartDate).setZone(this.timezone);
				this.date = start.toISODate();
				this.startTime = start.toFormat(format);
			}
			else {
				this.date = DateTime.local().setZone(this.timezone).toISODate();
			}
			if (this.eventInformation.eventEndDate) {
				this.endTime = DateTime.fromISO(this.eventInformation.eventEndDate).setZone(this.timezone).toFormat(format);
			}
		}
		if (!this.request && !this.eventInformation) {
			this.date = DateTime.local().setZone(this.timezone).toISODate();
		}
		this.updateCheckoutEventInfo();
		await this.getSchedulingRestrictions();
	}

	/**
	 * Reset end time if the selected start time is after it.
	 *
	 * @return {void}
	 */
	private updateStartTime(): void {
		if (this.endTime) {
			const format = this.$t('orders.guest_details.takeout.timepicker_format');
			const startTime = DateTime.fromFormat(this.startTime, format);
			const endTime = DateTime.fromFormat(this.endTime, format);
			if (startTime >= endTime) {
				this.endTime = '';
			}
		}
		this.updateCheckoutEventInfo();
	}

	/**
	 * When a user selects a date from the datepicker, update the date and reset the time
	 *
	 * @param {string} date
	 * @return {void}
	 */
	private updateTakeoutDate(date: string): void {
		if (this.scheduling) {
			this.scheduling.dueByDate = date;
			this.date = date;
			this.scheduling.dueByTime = '';
			this.startTime = '';
			this.endTime = '';
			this.updateCheckoutEventInfo();
		}
	}

	/**
	* Get all the scheduling restrictions from the menu/restaurant
	* and generate the time series with the interval.
	*
	* @return {Promise<void>}
	*/
	private async getSchedulingRestrictions(): Promise<void> {
		try {
			const scheduleRestrictions: ScheduleRestrictions = await this.fetchSchedulingRestrictions(this.items);
			const takeoutInfo: Pickup = {
				dueByDate: this.date,
				dueByTime: '',
				prepTime: scheduleRestrictions.prep_time,
				scheduled: scheduleRestrictions.scheduled_ordering,
				notes: ''
			};
			this.scheduling = new Scheduling(scheduleRestrictions, takeoutInfo, this.modifyingOrder, this.restaurant);
		}
		catch(error) {
			Toast.error(this.$t('orders.guest_details.error_fetching_scheduling_information'));
		}
		finally {
			this.$emit('fetching-scheduling-info', false);
			this.fetchingSchedulingInfo = false;
		}
	}

	/**
	 * Determine if a date is allowed to be selected in the datepicker
	 * The datepicker component will call this function for each date rendered
	 *
	 * @param {string} date - From the datepicker component in YYYY-MM-DD format
	 * @return {boolean}
	 */
	private dateAllowed(date: string): boolean {
		const dateAsDateTimeObj = DateTime.fromISO(date);

		if (this.scheduling) {
			// Allow the user to reselect the initial date if it's the same day, initalDateTimeSelection will only be set if the user is modifying an order
			if (this.scheduling.initialDateTimeSelection && dateAsDateTimeObj.hasSame(this.scheduling.initialDateTimeSelection, 'day')) {
				return true;
			}
			// If the date is in the past, disable it, original date excluded
			if (DateTime.local().toISODate() > dateAsDateTimeObj.toISODate()) {
				return false;
			}
			// Disable dates with no menu availability
			if (this.scheduling.disabledDays.includes(dateAsDateTimeObj.toJSDate().getDay())) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Update event info with delivery information from address delivery component
	 *
	 * @param {DeliveryInfo} info
	 * @return {void}
	 */
	private handleDeliveryInfoUpdated(info: DeliveryInfo): void {
		this.event.address = info.address || '';
		this.event.extra = info.extra || '';
		this.setDeliveryInformation(info);
		this.updateCheckoutEventInfo();
	}

	/**
	 * Update both event and takeout information in the checkout state.
	 *
	 * @return {void}
	 */
	private updateCheckoutEventInfo(): void {
		const format = this.$t('orders.guest_details.takeout.timepicker_format');
		this.event.eventStartDate = this.buildIsoDateTime(this.date, this.startTime, format);
		this.event.eventEndDate = this.buildIsoDateTime(this.date, this.endTime, format);
		this.setEventInformation(this.event);
		this.takeout.notes = this.takeoutInformation?.notes || this.takeout.notes;
		this.takeout.dueByDate = this.date;
		this.takeout.dueByTime = this.startTime;
		this.takeout.delivery = true;
		this.setTakeoutInformation(this.takeout);
	}

	/**
	 * Combine a date and time string into an ISO datetime string.
	 *
	 * @param {string} date
	 * @param {string} time
	 * @param {string} format
	 * @return {string}
	 */
	private buildIsoDateTime(date: string, time: string, timeFormat: string): string {
		if (!date || !time) {
			return '';
		}
		const dateObj = DateTime.fromISO(date);
		const timeObj = DateTime.fromFormat(time, timeFormat);
		return dateObj.set({ hour: timeObj.hour, minute: timeObj.minute, second: timeObj.second }).toISO();
	}
}
